import React, { ReactElement } from 'react';
import classnames from 'classnames/bind';
import { ImageTransformation, Option, TRANSFORMATIONS, Variant } from '@services';
import { OptionButton } from '@components';
import styles from './ProductOptions.module.scss';
import { Select, Tooltip, Typography, Option as GdOption } from 'gd-design-library';
import { ALL_IMAGES_TRANSFORMATIONS } from '@utils';

const CN = 'product-options';

const quickCartClassName = `${CN}--quick-cart`;
const labelClassName = `${CN}__label`;
const labelQuickCartClassName = `${labelClassName}--quick-cart`;

const selectClassName = `${CN}__select`;

const colorClassName = `${CN}__color`;
const colorInStockClassName = `${colorClassName}--in-stock`;
const colorOutOfStockClassName = `${colorClassName}--out-of-stock`;
const colorQuickCartClassName = `${colorClassName}--quick-cart`;

const colorImageClassName = `${CN}__color-image`;
const colorImageSelectedClassName = `${colorImageClassName}--selected`;
const colorImageQuickCartClassName = `${colorImageClassName}--quick-cart`;

const setClassName = `${CN}__set`;
const setItemClassName = `${CN}__set-item`;

const cx = classnames.bind(styles);

interface ProductOptionsProps {
  options: Option;
  selectedOption: string | EnumOptionValue;
  availability: Record<string, boolean>;
  outOfStockOptions: Record<string, boolean>;
  onClickOption: (optionName: string, optionValue: unknown) => void;
  variants: Variant[];
  quickCart?: boolean;
}

interface EnumOptionValue { key: string; label: string }

const renderOption = (
  type: Option['type'],
  value: unknown,
  isOptionSelected: boolean,
  isOptionAvailable: boolean,
  onClick: () => void,
  quickCart: boolean,
  isOutOfStock: boolean
): ReactElement | null => {
  let content;

  switch (type) {
  case 'boolean':
    content = value ? 'Yes' : 'No';
    break;
  case 'text':
    content = value as string;
    break;
  case 'number':
    content = value as number;
    break;
  case 'enum':
    content = (value as EnumOptionValue).label;
    break;
  case 'set':
    return Array.isArray(value) ? (
      <div className={ cx(setClassName) }>
        {value.map(item => (
          <Typography
            key={ String(item) }
            className={ cx(setItemClassName) }
          >
            {item}
          </Typography>
        ))}
      </div>
    ) : null;
  default:
    return null;
  }

  return (
    <OptionButton
      isOptionAvailable={ isOptionAvailable }
      isOptionSelected={ isOptionSelected }
      onClick={ onClick }
      quickCart={ quickCart }
      isOutOfStock={ isOutOfStock }
      key={ String(value) }
    >
      {content}
    </OptionButton>
  );
};

const generateSelectOptions = (value: unknown, optionsType: Option['type']): { value: string | number; name: string } => {
  switch (optionsType) {
  case 'text':
    return {
      value: value as string,
      name: String(value).toUpperCase()
    };
  case 'number':
    return {
      value: value as number,
      name: String(value).toUpperCase()
    };
  case 'enum':
    return {
      value: (value as EnumOptionValue).key,
      name: (value as EnumOptionValue).label.toUpperCase()
    };
  default:
    return {
      value: '',
      name: 'N/A'
    };
  }
};

export const ProductOptions: React.FC<ProductOptionsProps> = ({
  options,
  selectedOption,
  availability,
  outOfStockOptions,
  onClickOption,
  variants,
  quickCart = false }) => {
  const imageTransformation = new ImageTransformation();

  return (
    <div>
      <div className={ cx(quickCart ? labelQuickCartClassName : labelClassName) }>{options.label || options.name}:</div>
      <div className={ cx(CN, quickCart ? quickCartClassName : '') }>
        { quickCart && options.values.length > 6 && options.name !== 'color'
          ? (
            <div className={ cx(selectClassName) }>
              <Select
                items={ options.values.map(value => generateSelectOptions(value, options.type)) }
                value={ options.type === 'enum'
                  ? {
                    name: (selectedOption as EnumOptionValue).key,
                    value: (selectedOption as EnumOptionValue).key
                  } : {
                    name: selectedOption as string,
                    value: selectedOption as string
                  }
                }
                onChange={ (option: GdOption): void => (
                  onClickOption(
                    options.name,
                    options.type !== 'enum'
                      ? option.value
                      : options.values
                        .find(optionValue => (optionValue as EnumOptionValue).key === option.value)
                  )
                ) }
              />
            </div>
          )
          : options.values.map(value => {
            const isOptionSelected = selectedOption === value;
            const isOptionAvailable = availability?.[String(value)];
            const onClick = (): void => onClickOption(options.name, value);
            const colorVariants = variants?.find(variant => variant.attributes.color === value);
            const isOutOfStock = outOfStockOptions?.[String(value)];

            return options.name === 'color' ?
              <Tooltip
                content={ String(value) }
                key={ String(value) }
                position='bottom'
                gap={ 1 }
              >
                <div
                  onClick={ onClick }
                  role='button'
                  className={ cx(
                    colorClassName,
                    {
                      [colorInStockClassName]: !isOutOfStock,
                      [colorOutOfStockClassName]: isOutOfStock,
                      [colorQuickCartClassName]: quickCart
                    }
                  ) }
                >
                  <img
                    className={ cx(
                      colorImageClassName,
                      {
                        [colorImageSelectedClassName]: isOptionSelected,
                        [colorImageQuickCartClassName]: quickCart
                      }
                    ) }
                    src={ imageTransformation.transform(
                      colorVariants?.images[0],
                      [ ...ALL_IMAGES_TRANSFORMATIONS,
                        {
                          name: TRANSFORMATIONS.RESIZE,
                          params: { crop: 'fill', width: quickCart ? 24 : 60, height: quickCart ? 24 : 70 }
                        },
                        ...(quickCart ? [ {
                          name: TRANSFORMATIONS.RADIUS,
                          params: { radius: 12 }
                        } ] : []) ]
                    ) }
                  />
                </div>
              </Tooltip>
              : renderOption(
                options.type,
                value,
                isOptionSelected,
                isOptionAvailable,
                onClick,
                quickCart,
                isOutOfStock
              );
          })}
      </div>
    </div>
  );
};

